import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Star, Clock, MapPin, ArrowLeft, Calendar, ImageIcon, MessageCircle,
  Plane, Globe2, CheckCircle2, XCircle, Sparkles, ListChecks,
  Hotel, Image as ImgIcon, Info, Phone, Mail, Shield, Building,
} from 'lucide-react';
import { useNavigation } from '../contexts/NavigationContext';
import { GET_PACKAGE_DETAILS } from '../graphql/queries/package';
import Gallery from '../components/Gallery';
import ReviewList from '../components/ReviewList';
import Itinerary from '../components/Itinerary';
import PackageFormulas from '../components/PackageFormulas';
import PackageCard from '../components/PackageCard';
import DownloadPackageButton from '../components/pdf/DownloadPackageButton';
import Modal from '../components/Modal';
import ClientBookingFormModal from '../components/ClientBookingFormModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { TikTokIcon } from '../components/icons/TikTokIcon';
import SocialLinks from '../components/SocialLinks';
import { formatPrice, getDisplayPrice } from '../utils/price';
import { getImageUrl } from '../utils/imageUrl';
import type { 
  Formula, 
  SimilarPackage, 
  PackageDetails,
  Theme
} from '../types/pdf';
import { DEFAULT_THEME } from '../constants/theme';

const parseTheme = (theme: any): Partial<Theme> => {
  try {
    if (!theme) return {};
    
    // Si c'est un objet, nettoyer et valider les valeurs
    if (typeof theme === 'object' && theme !== null) {
      
      // Supprimer __typename et filtrer les valeurs nulles
      const { __typename, ...themeValues } = theme;
      const cleanTheme = Object.entries(themeValues).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key as keyof Theme] = value;
        }
        return acc;
      }, {} as Partial<Theme>);

      return cleanTheme;
    }
    
    // Si c'est une chaîne JSON, la parser
    if (typeof theme === 'string') {
      try {
        const parsedTheme = JSON.parse(theme);
        return parseTheme(parsedTheme); // Réutiliser la logique de nettoyage
      } catch (error) {
        console.error('Erreur de parsing JSON:', error);
        return {};
      }
    }
    
    console.warn('Format de thème non reconnu:', theme);
    return {};
  } catch (error) {
    console.error('Erreur lors du traitement du thème:', error, 'thème reçu:', theme);
    return {};
  }
};

interface PackageDetailPageProps {
  packageId: number;
}

export default function PackageDetailPage({ packageId }: PackageDetailPageProps) {
  const { navigateToAgency, navigateToPackage } = useNavigation();
  const [activeTab, setActiveTab] = useState<'overview' | 'itinerary' | 'gallery' | 'reviews'>('overview');
  const [selectedFormula, setSelectedFormula] = useState<Formula | null>(null);
  const [showReserverModal, setShowReserverModal] = useState(false);
  const [selectedDepartureId, setSelectedDepartureId] = useState<number | null>(null);

  const { loading, error, data } = useQuery<{ package: PackageDetails }>(
    GET_PACKAGE_DETAILS,
    {
      variables: { id: packageId },
      skip: !packageId,
      onError: (error) => {
        console.error('Erreur Apollo détaillée:', {
          message: error.message,
          graphQLErrors: error.graphQLErrors?.map(err => ({
            message: err.message,
            path: err.path,
            extensions: err.extensions
          })),
          networkError: error.networkError,
          extraInfo: error.extraInfo
        });
      }
    }
  );

  // ⚠ Tous les hooks doivent être déclarés AVANT les early returns ci-dessous,
  //   sinon React lève "Rendered more hooks than during the previous render".
  const sortedDepartures = React.useMemo(() => {
    const departures = data?.package?.departures;
    if (!departures || departures.length === 0) return [];
    const now = new Date();
    return [...departures]
      .map((dep) => ({
        ...dep,
        departureDate: new Date(
          typeof dep.departureDate === 'string' && !isNaN(Number(dep.departureDate))
            ? Number(dep.departureDate)
            : dep.departureDate
        ),
        returnDate: new Date(
          typeof dep.returnDate === 'string' && !isNaN(Number(dep.returnDate))
            ? Number(dep.returnDate)
            : dep.returnDate
        ),
      }))
      .sort((a, b) => {
        const aFuture = a.departureDate >= now;
        const bFuture = b.departureDate >= now;
        if (aFuture && !bFuture) return -1;
        if (!aFuture && bFuture) return 1;
        return a.departureDate.getTime() - b.departureDate.getTime();
      });
  }, [data?.package?.departures]);

  const formatDate = (dateString: string | number | Date | null | undefined) => {
    try {
      if (!dateString) {
        return 'Date à confirmer';
      }
      
      let date: Date;
      
      // Si c'est un timestamp sous forme de chaîne ou de nombre
      if (typeof dateString === 'string' && !isNaN(Number(dateString))) {
        date = new Date(Number(dateString));
      }
      // Si c'est un timestamp (nombre)
      else if (typeof dateString === 'number') {
        date = new Date(dateString);
      }
      // Si c'est déjà un objet Date
      else if (dateString instanceof Date) {
        date = dateString;
      }
      // Si c'est une chaîne de caractères (format date)
      else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        console.error('Date invalide:', dateString);
        return 'Date à confirmer';
      }
      
      return format(date, 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Erreur de formatage de date:', error, 'pour la date:', dateString);
      return 'Date à confirmer';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" color="blue-600" />
      </div>
    );
  }

  if (error || !data?.package) {
    console.error('❌ Erreur ou pas de données:', {
      error: error ? {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError
      } : null,
      data
    });
    return (
        <div className="min-h-screen flex flex-col items-center justify-center">
          <h2 className="text-2xl font-bold mb-4">Package non trouvé</h2>
        <p className="text-gray-600 mb-8">
          {error ? `Erreur: ${error.message}` : 'Le package que vous recherchez n\'existe pas ou a été supprimé.'}
        </p>
          <button
            onClick={navigateToAgency}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Retour à l'accueil
          </button>
        </div>
    );
  }

  const pkg = data.package;
  
  // Traiter le thème quelle que soit sa forme
  const agencyTheme = parseTheme(pkg.agency?.theme);
  
  // Fusionner avec le thème par défaut en s'assurant d'avoir des valeurs valides
  const theme = {
    ...DEFAULT_THEME,
    ...agencyTheme
  };



  // Vérifier que le thème est présent
  if (!pkg.agency?.theme) {
    console.log('Utilisation du thème par défaut car le thème de l\'agence est manquant');
  }

  // Départ actuellement affiché : choix utilisateur, sinon le prochain
  const nextDeparture =
    sortedDepartures.find((d) => d.id === selectedDepartureId) || sortedDepartures[0] || null;
  const hasMultipleDepartures = sortedDepartures.length > 1;

  // Mettre à jour le calcul de la durée
  const getDuration = () => {
    if (!nextDeparture) return { days: 0, nights: 0 };

    try {
      // Convertir les timestamps en nombres
      const departureTimestamp = typeof nextDeparture.departureDate === 'string' 
        ? parseInt(nextDeparture.departureDate) 
        : nextDeparture.departureDate;
        
      const returnTimestamp = typeof nextDeparture.returnDate === 'string' 
        ? parseInt(nextDeparture.returnDate) 
        : nextDeparture.returnDate;

      // Créer les objets Date à partir des timestamps
      const departureDate = new Date(departureTimestamp);
      const returnDate = new Date(returnTimestamp);
      
      if (isNaN(departureDate.getTime()) || isNaN(returnDate.getTime())) {
        console.error('Dates invalides après conversion:', {
          departure: {
            original: nextDeparture.departureDate,
            timestamp: departureTimestamp,
            date: departureDate
          },
          return: {
            original: nextDeparture.returnDate,
            timestamp: returnTimestamp,
            date: returnDate
          }
        });
        return { days: 0, nights: 0 };
      }
      
      const days = differenceInDays(returnDate, departureDate);
      return {
        days: Math.max(days, 0),
        nights: Math.max(days - 1, 0)
      };
    } catch (error) {
      console.error('Erreur de calcul de durée:', error, 'pour les dates:', {
        departure: {
          value: nextDeparture.departureDate,
          type: typeof nextDeparture.departureDate
        },
        return: {
          value: nextDeparture.returnDate,
          type: typeof nextDeparture.returnDate
        }
      });
      return { days: 0, nights: 0 };
    }
  };

  const { days, nights } = getDuration();

  const handleBackToAgency = () => {
    navigateToAgency();
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: theme.backgroundColor || DEFAULT_THEME.backgroundColor }}
    >
      {/* Bouton retour flottant */}
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={handleBackToAgency}
          style={{
            backgroundColor: theme.primaryColor || DEFAULT_THEME.primaryColor,
            color: theme.secondaryColor || DEFAULT_THEME.secondaryColor,
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
          }}
          className="flex items-center px-4 py-2.5 rounded-full hover:opacity-90 transition-all duration-200 font-medium text-sm backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Retour à l&apos;agence</span>
          <span className="sm:hidden">Retour</span>
        </button>
      </div>

      {/* ────── EN-TÊTE AGENCE (remplace l'ancien hero) ────── */}
      <div
        className="relative pt-20 pb-8"
        style={{
          background: `linear-gradient(135deg, ${theme.primaryColor || '#3B82F6'} 0%, ${theme.secondaryColor || '#1E40AF'} 100%)`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center gap-6 text-white">
            {/* Logo */}
            <div className="shrink-0">
              {pkg.agency?.logo ? (
                <div className="w-24 h-24 rounded-2xl bg-white p-2 shadow-lg">
                  <img
                    src={getImageUrl(pkg.agency.logo)}
                    alt={pkg.agency.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
                  <Building className="w-12 h-12 text-white" />
                </div>
              )}
            </div>

            {/* Nom + contact */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-bold drop-shadow">
                  {pkg.agency?.name}
                </h1>
                {pkg.agency?.verified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold backdrop-blur-md bg-white/20 border border-white/30">
                    <Shield className="w-3.5 h-3.5" />
                    Vérifié
                  </span>
                )}
                {pkg.agency?.rating > 0 && (
                  <span className="inline-flex items-center gap-1 text-sm">
                    <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" />
                    <span className="font-semibold">{pkg.agency.rating.toFixed(1)}</span>
                    {pkg.agency.reviewCount > 0 && (
                      <span className="text-white/80 text-xs">({pkg.agency.reviewCount} avis)</span>
                    )}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/90">
                {pkg.agency?.address && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 shrink-0" />
                    <span>{pkg.agency.address}</span>
                  </div>
                )}
                {pkg.agency?.phone && (
                  <a href={`tel:${pkg.agency.phone}`} className="flex items-center gap-1.5 hover:underline">
                    <Phone className="w-4 h-4 shrink-0" />
                    <span>{pkg.agency.phone}</span>
                  </a>
                )}
                {pkg.agency?.email && (
                  <a href={`mailto:${pkg.agency.email}`} className="flex items-center gap-1.5 hover:underline">
                    <Mail className="w-4 h-4 shrink-0" />
                    <span>{pkg.agency.email}</span>
                  </a>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ────── TABS ────── */}
      <div className="bg-white border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {([
              { key: 'overview',  label: 'Résumé',     icon: Info },
              { key: 'itinerary', label: 'Itinéraire', icon: ListChecks },
              { key: 'gallery',   label: 'Galerie',    icon: ImgIcon },
              { key: 'reviews',   label: 'Avis',       icon: MessageCircle },
            ] as const).map(({ key, label, icon: Icon }) => {
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    color: active ? theme.primaryColor : undefined,
                    borderBottom: active
                      ? `3px solid ${theme.primaryColor}`
                      : '3px solid transparent',
                  }}
                  className="flex items-center gap-2 py-4 px-4 font-medium text-gray-600 hover:text-gray-900 transition-colors -mb-px text-sm whitespace-nowrap"
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="space-y-6">
            {/* Aperçu général du package */}
            <article className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-5 sm:items-start">
                <div className="sm:col-span-2 bg-gray-100 aspect-[4/3]">
                  <img
                    src={getImageUrl(pkg.image)}
                    alt={pkg.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="sm:col-span-3 p-6 flex flex-col gap-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                      Aperçu du voyage
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 leading-snug">
                      {pkg.title}
                    </h2>
                  </div>

                  {/* Sélecteur de départ — visible si plusieurs départs */}
                  {hasMultipleDepartures && (
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                        Choisir un départ ({sortedDepartures.length} disponibles)
                      </label>
                      <select
                        value={nextDeparture?.id ?? ''}
                        onChange={(e) => setSelectedDepartureId(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:outline-none focus:ring-2"
                        style={{
                          // @ts-ignore - CSS var pour focus ring couleur agence
                          ['--tw-ring-color' as any]: theme.primaryColor,
                        }}
                      >
                        {sortedDepartures.map((dep) => {
                          const isPast = dep.departureDate < new Date();
                          return (
                            <option key={dep.id} value={dep.id}>
                              Du {formatDate(dep.departureDate)} au {formatDate(dep.returnDate)}
                              {isPast ? ' (passé)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <KeyFact
                      icon={Calendar}
                      label="Départ"
                      value={nextDeparture ? formatDate(nextDeparture.departureDate) : 'Dates à venir'}
                      accent={theme.primaryColor}
                    />
                    <KeyFact
                      icon={Calendar}
                      label="Retour"
                      value={nextDeparture ? formatDate(nextDeparture.returnDate) : 'Dates à venir'}
                      accent={theme.primaryColor}
                    />
                    <KeyFact
                      icon={Clock}
                      label="Durée"
                      value={days > 0 ? `${days}j / ${nights}n` : '—'}
                      accent={theme.primaryColor}
                    />
                    <KeyFact
                      icon={Plane}
                      label="Départ de"
                      value={pkg.departure || 'À confirmer'}
                      accent={theme.primaryColor}
                    />
                    {pkg.countries?.length > 0 && (
                      <div className="col-span-2">
                        <KeyFact
                          icon={Globe2}
                          label="Pays"
                          value={pkg.countries.join(' • ')}
                          accent={theme.primaryColor}
                        />
                      </div>
                    )}
                    {pkg.airline && (
                      <div className="col-span-2">
                        <KeyFact
                          icon={Plane}
                          label="Compagnie aérienne"
                          value={
                            pkg.airline.code
                              ? `${pkg.airline.name} (${pkg.airline.code})`
                              : pkg.airline.name
                          }
                          accent={theme.primaryColor}
                        />
                      </div>
                    )}
                  </div>
                  <DownloadPackageButton
                    package={pkg}
                    agency={pkg.agency}
                    className="mt-auto"
                    theme={theme}
                  />
                </div>
              </div>
            </article>

            {activeTab === 'overview' && (
              <>
                {/* Description */}
                <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
                  <SectionTitle icon={Info} title="À propos de ce voyage" accent={theme.primaryColor} />
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">{pkg.description}</p>
                </article>

                {/* Points forts */}
                {pkg.highlights?.length > 0 && (
                  <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
                    <SectionTitle icon={Sparkles} title="Points forts" accent={theme.primaryColor} />
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {pkg.highlights.map((highlight: string, index: number) => (
                        <li
                          key={index}
                          className="flex items-start gap-3 text-gray-700 bg-gray-50 rounded-xl p-3"
                        >
                          <CheckCircle2
                            className="w-5 h-5 mt-0.5 shrink-0"
                            style={{ color: theme.primaryColor }}
                          />
                          <span className="text-sm">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                )}

                {/* Inclus / Non inclus */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
                    <SectionTitle
                      icon={CheckCircle2}
                      title="Inclus dans le prix"
                      accent={theme.primaryColor}
                    />
                    {pkg.included?.length > 0 ? (
                      <ul className="space-y-2.5">
                        {pkg.included.map((item: string, index: number) => (
                          <li key={index} className="flex items-start gap-3 text-gray-700">
                            <CheckCircle2 className="w-4 h-4 mt-1 shrink-0 text-emerald-500" />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Non précisé</p>
                    )}
                  </article>

                  <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
                    <SectionTitle
                      icon={XCircle}
                      title="Non inclus"
                      accent={theme.secondaryColor}
                    />
                    {pkg.excluded?.length > 0 ? (
                      <ul className="space-y-2.5">
                        {pkg.excluded.map((item: string, index: number) => (
                          <li key={index} className="flex items-start gap-3 text-gray-700">
                            <XCircle className="w-4 h-4 mt-1 shrink-0 text-rose-400" />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Non précisé</p>
                    )}
                  </article>
                </div>

                {/* Formules — mis en évidence */}
                <article
                  className="bg-white rounded-2xl shadow-xl border-2 p-6 sm:p-8 relative overflow-hidden"
                  style={{ borderColor: theme.primaryColor }}
                >
                  {/* Bandeau accent en haut */}
                  <div
                    className="absolute top-0 left-0 right-0 h-1.5"
                    style={{
                      background: `linear-gradient(90deg, ${theme.primaryColor}, ${theme.secondaryColor || theme.primaryColor})`,
                    }}
                  />
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <SectionTitle
                      icon={Hotel}
                      title="Choisissez votre formule"
                      subtitle="Comparez les options et réservez en un clic"
                      accent={theme.primaryColor}
                    />
                    <span
                      className="shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mt-1"
                      style={{
                        backgroundColor: `${theme.primaryColor}15`,
                        color: theme.primaryColor,
                      }}
                    >
                      {pkg.formulas?.length || 0} {pkg.formulas?.length > 1 ? 'formules' : 'formule'}
                    </span>
                  </div>
                  <PackageFormulas
                    formulas={pkg.formulas}
                    selectedFormulaId={selectedFormula?.id}
                    onSelectFormula={setSelectedFormula}
                    onReserve={(formula) => {
                      setSelectedFormula(formula);
                      setShowReserverModal(true);
                    }}
                    theme={theme}
                  />
                </article>
              </>
            )}

            {activeTab === 'itinerary' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {pkg.itinerary && pkg.itinerary.length > 0 ? (
                  <Itinerary itinerary={pkg.itinerary} />
                ) : (
                  <EmptyState
                    icon={ListChecks}
                    title="Itinéraire détaillé bientôt disponible"
                    description="L'agence finalise le programme jour par jour. Contactez-la pour en savoir plus."
                  />
                )}
              </div>
            )}

            {activeTab === 'gallery' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <EmptyState
                  icon={ImageIcon}
                  title="Galerie photo bientôt disponible"
                  description="L'agence ajoutera prochainement des photos de cette destination."
                />
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <EmptyState
                  icon={MessageCircle}
                  title="Avis bientôt disponibles"
                  description="Les retours des voyageurs ayant déjà fait ce voyage apparaîtront ici."
                />
              </div>
            )}
        </div>

        {/* Packages similaires */}
        {pkg.similarPackages && pkg.similarPackages.length > 0 && (
          <section className="mt-16 pt-12 border-t border-gray-200">
            <h2 
              className="text-2xl font-bold mb-2" 
              style={{ color: theme.textColor || DEFAULT_THEME.textColor }}
            >
              Packages similaires
            </h2>
            <div 
              className="h-1 w-16 rounded-full mb-8" 
              style={{ backgroundColor: theme.primaryColor }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {pkg.similarPackages.map(similarPackage => (
                <PackageCard 
                  key={similarPackage.id} 
                  package={similarPackage}
                  primaryColor={theme.primaryColor}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Social Links */}
      <SocialLinks
        facebook={pkg.agency.facebook}
        instagram={pkg.agency.instagram}
        tiktok={pkg.agency.tiktok}
        primaryColor={theme.primaryColor}
      />

      <ClientBookingFormModal
        isOpen={showReserverModal}
        onClose={() => setShowReserverModal(false)}
        pkg={pkg}
        selectedFormula={selectedFormula}
        theme={{
          primaryColor: theme.primaryColor,
          secondaryColor: theme.secondaryColor,
        }}
      />
    </div>
  );
}

// ─── Helpers UI ────────────────────────────────────────────────────────────

function KeyFact({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: any;
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: accent || '#2563eb' }} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
          {label}
        </div>
        <div className="text-sm font-medium text-gray-900 truncate">{value}</div>
      </div>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  subtitle,
  accent,
}: {
  icon: any;
  title: string;
  subtitle?: string;
  accent?: string;
}) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{
          backgroundColor: accent ? `${accent}15` : '#eff6ff',
          color: accent || '#2563eb',
        }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: any;
  title: string;
  description: string;
}) {
  return (
    <div className="p-16 text-center">
      <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gray-50 flex items-center justify-center">
        <Icon className="w-10 h-10 text-gray-300" />
      </div>
      <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 max-w-md mx-auto">{description}</p>
    </div>
  );
}